import { useTranslation } from 'next-i18next/pages';

import useCurrentLang from '@/hooks/apps/useCurrentLang';
import useSiteSettings from '@/hooks/apps/useSiteSettings';

import { COMPANY_ADDRESS } from '@/data/shared/companyLocation';

import formatBusinessNumber from '@/utils/formatBusinessNumber';
import formatPhoneNumber from '@/utils/formatPhoneNumber';

import type { FooterTone } from './footerTheme';

/**
 * ### FooterVariant
 *
 * Which footer hosts the part. Desktop leaves the company name unlabelled and
 * keeps the registration number beside the contact fields; mobile labels the
 * name and gives the registration number its own row.
 */
export type FooterVariant = 'desktop' | 'mobile';

/**
 * ### FooterCompanyInfoProps
 *
 * What the block needs from the surrounding footer.
 */
export interface FooterCompanyInfoProps {
  /**
   * ### tone
   *
   * Resolved colours for the footer this block sits in.
   */
  tone: FooterTone;

  /**
   * ### variant
   *
   * Layout to use.
   */
  variant: FooterVariant;
}

const INFO_ITEM = 'flex gap-1 font-medium leading-[140%] tracking-[-0.28px]';
const INFO_TEXT = 'text-sm leading-[150%]';

/**
 * ### FooterCompanyInfo
 *
 * Company registration details, read from the site settings an administrator
 * fills in.
 *
 * This block owns the settings request on purpose. The footer used to withhold
 * everything — site map, policy links, the lot — until the request came back,
 * so a slow, failed, or unfilled response left visitors with no way out of the
 * page. Only the fields that genuinely come from settings wait on them now.
 *
 * @param props Footer tone and layout
 * @returns The company information rows, or nothing until settings arrive
 */
export function FooterCompanyInfo({ tone, variant }: FooterCompanyInfoProps) {
  const { t } = useTranslation('common');
  const currentLang = useCurrentLang();
  const { data: settingData, isLoading } = useSiteSettings();

  if (isLoading) return null;

  if (!settingData || Object.keys(settingData).length === 0) {
    // 관리자에게만 의미가 있는 안내라 방문자용 번역 키를 만들지 않는다.
    return (
      <p className="text-sm text-apps-coreProductError01">
        푸터 정보를 입력해 주세요.
      </p>
    );
  }

  const label = `${INFO_TEXT} ${tone.label}`;
  const value = `${INFO_TEXT} ${tone.value}`;
  /** 설정에 아직 없는 항목은 화면에서 빈칸으로 두고, 포맷 함수에는 넘기지 않는다. */
  const read = (key: string) => settingData[`${key}_${currentLang}`]?.set_value;
  const phone = read('site_mobile');
  const registration = read('site_num');
  const isMobile = variant === 'mobile';

  return (
    <div className={`flex flex-col ${isMobile ? 'gap-2' : 'gap-4'}`}>
      <div className="flex gap-3">
        <div className={INFO_ITEM}>
          {isMobile && <p className={label}>{t('상호명')}</p>}
          <div className={value}>{read('site_title')}</div>
        </div>
        <div className={INFO_ITEM}>
          <p className={label}>{t('대표번호')}</p>
          <div className={value}>{phone ? formatPhoneNumber(phone) : ''}</div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className={INFO_ITEM}>
          <p className={label}>{t('주소')}</p>
          <div className={value}>{COMPANY_ADDRESS[currentLang]}</div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className={INFO_ITEM}>
          <p className={label}>{t('대표자')}</p>
          <div className={value}>{read('site_ceo')}</div>
        </div>
        <div className={INFO_ITEM}>
          <p className={label}>{t('이메일')}</p>
          <div className={value}>{read('site_email')}</div>
        </div>
        {!isMobile && (
          <div className={INFO_ITEM}>
            <p className={label}>{t('사업자등록번호')}</p>
            <div className={value}>
              {registration ? formatBusinessNumber(registration) : ''}
            </div>
          </div>
        )}
      </div>

      {isMobile && (
        <div className="flex gap-3">
          <div className={INFO_ITEM}>
            <p className={label}>{t('사업자등록번호')}</p>
            <div className={value}>
              {registration ? formatBusinessNumber(registration) : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ### FooterCopyright
 *
 * Copyright line. Only the holder's name comes from site settings, so the line
 * stays on the page while settings are missing instead of disappearing with
 * them.
 *
 * @param props Footer layout, which decides the sentence each platform has
 *   always shown
 * @returns The copyright sentence
 */
export function FooterCopyright({ variant }: { variant: FooterVariant }) {
  const currentLang = useCurrentLang();
  const { data: settingData } = useSiteSettings();
  const holder = settingData?.[`site_copyright_${currentLang}`]?.set_value ?? '';

  return (
    <div className="text-xs leading-[130%] tracking-[-0.24px] text-apps-coreGray03">
      {variant === 'mobile'
        ? `Copyright 2024.${holder}. All rights reserved.`
        : `© 2024. ${holder}. All rights reserved.`}
    </div>
  );
}
